import { endOfYesterday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import {
  CheckSquare,
  Clock,
  Flame,
  Layers,
  Tag,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { FilterCategory } from "../filters/FilterCategory";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActiveFilterButton } from "../misc/ActiveFilterButton";
import type { Population, Sale, Temperature } from "../types";

// Sprint 3 T3.1 — Filtres Population / Température / Sales.
// Couleurs alignées sur ContactHeader / TemperaturePopover Sprint 2.
const POPULATION_OPTIONS: { value: Population; label: string; dot: string }[] =
  [
    { value: "plateforme", label: "Plateforme", dot: "bg-green-500" },
    { value: "prospection", label: "Prospection", dot: "bg-orange-500" },
  ];

const TEMPERATURE_OPTIONS: {
  value: Temperature;
  label: string;
  dot: string;
}[] = [
  { value: "chaud", label: "Chaud", dot: "bg-red-500" },
  { value: "a_chauffer", label: "À chauffer", dot: "bg-orange-500" },
  { value: "froid", label: "Froid", dot: "bg-blue-500" },
  { value: "perdu", label: "Perdu", dot: "bg-muted-foreground" },
];

const FilterDotLabel = ({ dot, label }: { dot: string; label: string }) => (
  <span className="inline-flex items-center gap-2">
    <span className={cn("size-2 rounded-full", dot)} />
    {label}
  </span>
);

export const ContactListFilter = () => {
  const { noteStatuses } = useConfigurationContext();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  // Reprise 07/09/2026 — perPage 10 → 100 : les tags de campagne (août 2026)
  // n'entraient pas dans les 10 premiers par ordre alphabétique, donc aucune
  // campagne n'était filtrable, même sur mobile.
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.contacts.filters.search"),
      }}
    >
      <FilterCategory
        label="resources.contacts.fields.last_seen"
        icon={<Clock />}
      >
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.today"
          value={{
            "last_seen@gte": endOfYesterday().toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.this_week"
          value={{
            "last_seen@gte": startOfWeek(new Date()).toISOString(),
            "last_seen@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_week"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfWeek(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_this_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": startOfMonth(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label="resources.contacts.filters.before_last_month"
          value={{
            "last_seen@gte": undefined,
            "last_seen@lte": subMonths(
              startOfMonth(new Date()),
              1,
            ).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory
        label="resources.notes.fields.status"
        icon={<TrendingUp />}
      >
        {noteStatuses.map((status) => (
          <ToggleFilterButton
            key={status.value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={
              <span>
                {status.label} <Status status={status.value} />
              </span>
            }
            value={{ status: status.value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory label="resources.contacts.filters.tags" icon={<Tag />}>
        {data &&
          data.map((record) => (
            <ToggleFilterButton
              className="w-auto md:w-full justify-between h-10 md:h-8"
              key={record.id}
              label={
                <Badge
                  variant="secondary"
                  className="text-black text-sm md:text-xs font-normal cursor-pointer"
                  style={{
                    backgroundColor: record?.color,
                  }}
                >
                  {record?.name}
                </Badge>
              }
              value={{ "tags@cs": `{${record.id}}` }}
              size={isMobile ? "lg" : undefined}
            />
          ))}
      </FilterCategory>

      <FilterCategory icon={<Layers />} label="Population">
        {POPULATION_OPTIONS.map((opt) => (
          <ToggleFilterButton
            key={opt.value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={<FilterDotLabel dot={opt.dot} label={opt.label} />}
            value={{ population: opt.value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory icon={<Flame />} label="Température">
        {TEMPERATURE_OPTIONS.map((opt) => (
          <ToggleFilterButton
            key={opt.value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={<FilterDotLabel dot={opt.dot} label={opt.label} />}
            value={{ temperature: opt.value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<CheckSquare />}
        label="resources.contacts.filters.tasks"
      >
        <ToggleFilterButton
          className="w-full justify-between h-10 md:h-8"
          label="resources.tasks.filters.with_pending"
          value={{ "nb_tasks@gt": 0 }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory
        icon={<Users />}
        label="resources.contacts.fields.sales_id"
      >
        <ToggleFilterButton
          className="w-full justify-between h-10 md:h-8"
          label="crm.common.me"
          value={{ sales_id: identity?.id }}
          size={isMobile ? "lg" : undefined}
        />
        {salesList
          ?.filter((s) => s.id !== identity?.id)
          .map((sale) => (
            <ToggleFilterButton
              key={sale.id}
              className="w-auto md:w-full justify-between h-10 md:h-8"
              label={
                `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim() || "—"
              }
              value={{ sales_id: sale.id }}
              size={isMobile ? "lg" : undefined}
            />
          ))}
      </FilterCategory>
    </ResponsiveFilters>
  );
};

export const ContactListFilterSummary = () => {
  const { noteStatuses } = useConfigurationContext();
  const { identity } = useGetIdentity();
  // Reprise 07/09/2026 — perPage 10 → 100 : les tags de campagne (août 2026)
  // n'entraient pas dans les 10 premiers par ordre alphabétique, donc aucune
  // campagne n'était filtrable, même sur mobile.
  const { data } = useGetList("tags", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: salesList } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });
  const { filterValues } = useListContext();
  const hasFilters = !!Object.entries(filterValues || {}).filter(
    ([key]) => key !== "q",
  ).length;

  if (!hasFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start mb-4 gap-1">
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.today"
        value={{
          "last_seen@gte": endOfYesterday().toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.this_week"
        value={{
          "last_seen@gte": startOfWeek(new Date()).toISOString(),
          "last_seen@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_week"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfWeek(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_this_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": startOfMonth(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.before_last_month"
        value={{
          "last_seen@gte": undefined,
          "last_seen@lte": subMonths(startOfMonth(new Date()), 1).toISOString(),
        }}
      />

      {noteStatuses.map((status) => (
        <ActiveFilterButton
          key={status.value}
          className="w-auto justify-between h-8"
          label={
            <span>
              {status.label} <Status status={status.value} />
            </span>
          }
          value={{ status: status.value }}
        />
      ))}

      {data &&
        data.map((record) => (
          <ActiveFilterButton
            className="w-auto justify-between h-8"
            key={record.id}
            label={
              <Badge
                variant="secondary"
                className="text-black text-sm md:text-xs font-normal cursor-pointer"
                style={{
                  backgroundColor: record?.color,
                }}
              >
                {record?.name}
              </Badge>
            }
            value={{ "tags@cs": `{${record.id}}` }}
          />
        ))}

      {POPULATION_OPTIONS.map((opt) => (
        <ActiveFilterButton
          key={opt.value}
          className="w-auto justify-between h-8"
          label={<FilterDotLabel dot={opt.dot} label={opt.label} />}
          value={{ population: opt.value }}
        />
      ))}

      {TEMPERATURE_OPTIONS.map((opt) => (
        <ActiveFilterButton
          key={opt.value}
          className="w-auto justify-between h-8"
          label={<FilterDotLabel dot={opt.dot} label={opt.label} />}
          value={{ temperature: opt.value }}
        />
      ))}

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.tasks.filters.with_pending"
        value={{ "nb_tasks@gt": 0 }}
      />

      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label="resources.contacts.filters.managed_by_me"
        value={{ sales_id: identity?.id }}
      />

      {salesList
        ?.filter((s) => s.id !== identity?.id)
        .map((sale) => (
          <ActiveFilterButton
            key={sale.id}
            className="w-auto justify-between h-8"
            label={
              `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim() || "—"
            }
            value={{ sales_id: sale.id }}
          />
        ))}
    </div>
  );
};
